import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import { useLocation, useParams } from "react-router-dom";
import emptyResult from "../../assets/img/cuate@2x.png";
import history from "../../routes/history";
import "./ManageJobsInvited.css";
import Loader from "../../components/Loading/Loader";
import {
  ManageJobsInvitedCard,
  ManageJobsInvitedEmptyState,
  ManageJobsInvitedHeader,
  ManageJobsInvitedSearchBar,
  ManageJobsInvitedSortDropdown,
} from "./components";
import PlaceBidModal from "../QueryPage/components/organisms/PlaceBidModal";
import { getInvitedTalentsByJobApi } from "../../API/API";

const normalizeExternalTalent = (item) => {
  try {
    const nameParts = (item.full_name || '').trim().split(' ');
    return {
      _id: item.profileId,
      _type: 'external',
      _linkedinUrl: item.linkedinUrl,
      Title: item.title,
      ClientRate: null,
      BudgetTypes: { BudgetTypeName: '' },
      uiLocation: [item.location, item.country].filter(Boolean).join(', '),
      uiStatus: item.inviteStatus || 'Invited',
      uiAmount: '$Negotiable',
      uiPrimaryAction: 'Send Reminder',
      uiPrimaryActionDisabled: true,
      users: {
        Profiles: {
          FirstName: nameParts[0] || '',
          LastName: nameParts.slice(1).join(' ') || '',
          PictureUrl: item.profile_picture_url || null,
          ExperienceLevelName: { YearsOfExperienceName: '' },
          Rating: 0,
          Reviews: 0,
        },
      },
      InvitedBy: item.initialInvite
        ? [{ EmployerEmail: item.initialInvite.jobTitle || '', InviteDate: item.initialInvite.sentAt }]
        : [],
    };
  } catch {
    return null;
  }
};
 

const PAGE_SIZE = 4;
const SORT_OPTIONS = {
  most_relevant: { label: "Most Relevant", value: "MOST_RELEVANT" },
  most_recent: { label: "Most Recent", value: "MOST_RECENT" },
};

export default function ManageJobsInvited() {
  const location = useLocation();
  const { id: routeJobId } = useParams();
  const routeState = location?.state || {};
  const jobId = routeJobId || routeState?.jobOrderId || routeState?.id || "";

  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("most_relevant");
  const [serverCandidates, setServerCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverTotal, setServerTotal] = useState(0);
  const [bidModal, setBidModal] = useState({
    open: false,
    talent: null,
    talentType: "external",
    skillListingId: null,
  });

  const fetchInvitedTalents = async ({
    nextPage = page,
    nextSortKey = sortKey,
    nextJobId = jobId,
  } = {}) => {
    if (!nextJobId) {
      setServerCandidates([]);
      setServerTotal(0);
      return;
    }

    setLoading(true);
    try {
      const skip = (nextPage - 1) * PAGE_SIZE;
      const response = await getInvitedTalentsByJobApi({
        jobId: nextJobId,
        skip,
        limit: PAGE_SIZE,
        matchesSkip: 0,
        matchesLimit: 0,
        sorttype: SORT_OPTIONS[nextSortKey]?.value,
      });

      const payload = response?.data || {};
      const externalTalents = (payload?.externalInvitedTalents || []).map(normalizeExternalTalent).filter(Boolean);
      setServerCandidates(externalTalents);
      setServerTotal(payload?.counts?.externalInvitedTalents || externalTalents.length);
    } catch (error) {
      setServerCandidates([]);
      setServerTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitedTalents({ nextPage: 1, nextSortKey: sortKey, nextJobId: jobId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countValue = serverTotal || serverCandidates.length;
  const titleText = `${countValue} Invited`;
  const subtitleText = `You have invited ${countValue} talents for a job of "${
    routeState?.title || "Travel Nurse RN - Med Surg Tele"
  }".`;

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    fetchInvitedTalents({ nextPage, nextSortKey: sortKey, nextJobId: jobId });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSortChange = ({ key }) => {
    if (!SORT_OPTIONS[key]) return;
    setSortKey(key);
    setPage(1);
    fetchInvitedTalents({ nextPage: 1, nextSortKey: key, nextJobId: jobId });
  };

  const handleViewProfile = (candidate) => {
    const profileId = candidate?.SkillListingId || candidate?._id;
    if (!profileId) return;
    history.push(`/external-detail/${profileId}`);
  };

  const handlePlaceBid = (candidate) => {
    if (candidate?._type === 'external') return;
    const firstName = candidate?.users?.Profiles?.FirstName || "";
    const lastName = candidate?.users?.Profiles?.LastName || "";
    const name = `${firstName} ${lastName}`.trim() || candidate?.users?.UserName || "Candidate";
    const skillListingId =
      candidate?.SkillListingId ||
      candidate?.skill_listings?.SkillListingId ||
      candidate?.skill_listings?._id ||
      candidate?._id ||
      null;

    const avatar = candidate?.users?.Profiles?.PictureUrl || "";

    setBidModal({
      open: true,
      talentType: "external",
      skillListingId,
      talent: {
        ...candidate,
        name,
        title: candidate?.Title || "RN - Med Surg",
        avatar,
        initials: `${firstName?.[0] || ""}${lastName?.[0] || ""}`.trim().toUpperCase() || "BW",
      },
    });
  };

  return (
    <section className="manage-jobs-invited-page">
      {loading ? (
        <div className="manage-jobs-invited-page__screen-loader">
          <Loader />
        </div>
      ) : null}

      <div className="manage-jobs-invited-page__shell">
        <ManageJobsInvitedHeader title={titleText} subtitle={subtitleText} />

        <div className="manage-jobs-invited-page__toolbar">
          <ManageJobsInvitedSearchBar
            value=""
            onChange={() => {}}
            placeholder="Search candidate"
          />

          <div className="manage-jobs-invited-page__toolbar-actions">
            <ManageJobsInvitedSortDropdown
              label={SORT_OPTIONS[sortKey]?.label || "Most Relevant"}
              onChange={handleSortChange}
            />
          </div>
        </div>

        {serverCandidates?.length ? (
          <>
            <div className="manage-jobs-invited-page__grid">
              {serverCandidates.map((candidate, index) => (
                <ManageJobsInvitedCard
                  key={candidate?._id || candidate?.SkillListingId || index}
                  candidate={candidate}
                  onViewProfile={handleViewProfile}
                  onPlaceBid={handlePlaceBid}
                />
              ))}
            </div>

            {serverTotal > PAGE_SIZE ? (
              <div className="manage-jobs-invited-page__pagination">
                <Pagination
                  current={page}
                  total={serverTotal}
                  pageSize={PAGE_SIZE}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
            ) : null}
          </>
        ) : (
          <ManageJobsInvitedEmptyState
            image={emptyResult}
            title="No invited candidates found"
            description={"Invited candidates will appear here once outreach has been sent."}
          />
        )}

        <PlaceBidModal
          open={bidModal.open}
          talent={bidModal.talent}
          talentType={bidModal.talentType}
          skillListingId={bidModal.skillListingId}
          onClose={() =>
            setBidModal({
              open: false,
              talent: null,
              talentType: "external",
              skillListingId: null,
            })
          }
        />
      </div>
    </section>
  );
}
